"use client";

import { useState } from "react";
import { I, Avatar, Mention } from "@/components/kamibot-v2/discord";
import { Container, SectionHead } from "@/components/kamibot-v2/common";

type MiniMsgProps = {
  user: "kami" | string;
  name: string;
  isBot?: boolean;
  children: React.ReactNode;
  color?: string;
};

// kept for parity with the design prototype; not currently rendered by the
// Features section but exported so other groups can reuse it if needed.
export function MiniMsg({ user, name, isBot, children, color }: MiniMsgProps) {
  return (
    <div className="mini-msg">
      {user === "kami" ? (
        <Avatar />
      ) : (
        <div className={`av ${color || "user"}`}>{user}</div>
      )}
      <div>
        <div className="head">
          <span className={`name ${isBot ? "bot" : ""}`}>{name}</span>
        </div>
        <div className="text">{children}</div>
      </div>
    </div>
  );
}

function FeatureAI() {
  return (
    <div className="feature feature-ai">
      <span className="f-tag">
        <I.sparkle /> 멘션 @카미봇
      </span>
      <h3 className="f-title">말로 시키면, 서버가 정리돼요</h3>
      <p className="f-sub">
        카미봇은 단순한 채팅 AI가 아니에요. <b>서버 구조 자체를 이해하고 변경</b>할 수 있어요.
        채널 만들기, 권한 맞추기, 카테고리 옮기기 — 클릭하지 않고 부탁해요.
      </p>
      <div className="f-demo">
        <div className="ai-bubble">
          <Mention>@카미봇</Mention> 자동생성 통화방 하나 만들고 음성 카테고리에 넣은 다음
          잡담방이랑 권한 똑같이 맞춰줘
        </div>
        <div className="ai-plan">
          <div className="ai-plan-head">
            <span className="dot" /> 작업 계획 <span className="muted">· 4단계</span>
          </div>
          <ol>
            <li>
              <span className="ai-tick">✓</span>
              <span>
                <code>#잡담방</code> 권한 분석 —{" "}
                <span className="muted">@everyone, @멤버, @모더레이터</span>
              </span>
            </li>
            <li>
              <span className="ai-tick">✓</span>
              <span>
                음성 카테고리에 <code>🎙️ 자동생성</code> 채널 생성
              </span>
            </li>
            <li>
              <span className="ai-tick">✓</span>
              <span>
                자동생성 트리거 활성화 —{" "}
                <span className="muted">참여 시 새 채널 복제</span>
              </span>
            </li>
            <li>
              <span className="ai-tick">✓</span>
              <span>권한 3개 동기화 완료</span>
            </li>
          </ol>
        </div>
        <div className="ai-bubble bot ai-confirm">
          <span className="ai-confirm-ic">
            <I.check />
          </span>
          <div>
            <b>완료했어요.</b> 누가 들어가면 자기 이름이 붙은 새 통화방이 생겨요. 잡담방이랑
            똑같이 모더레이터만 음소거 권한 가져요.
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureTTS() {
  const heights = [10, 22, 14, 26, 18, 12, 28, 16, 20, 14, 24, 12, 22, 18, 14, 26, 18, 10, 22, 14];
  const voices = ["あいえる", "사쿠라", "민준", "Kana", "Aria"];
  const [voice, setVoice] = useState(0);

  return (
    <div className="feature feature-tts">
      <span className="f-tag">
        <I.tts /> TTS /tts
      </span>
      <h3 className="f-title">목소리가 많은 TTS</h3>
      <p className="f-sub">
        VOICEVOX, ElevenLabs 등 200개가 넘는 목소리 중에서 우리 서버에 맞는 톤을 골라서 읽어줘요.
      </p>
      <div className="f-demo">
        <div className="tts-row">
          <span className="play">
            <I.play />
          </span>
          <div className="wave">
            {heights.map((h, i) => (
              <i
                key={i}
                style={
                  {
                    "--h": `${h}px`,
                    animationDelay: `${i * 0.07}s`,
                  } as React.CSSProperties
                }
              />
            ))}
          </div>
          <span className="voice">0:08 · {voices[voice]}</span>
        </div>
        <div className="voice-list">
          {voices.map((v, i) => (
            <span
              key={v}
              className={`voice-chip ${i === voice ? "active" : ""}`}
              onClick={() => setVoice(i)}
            >
              {v}
            </span>
          ))}
          <span className="voice-chip">+196</span>
        </div>
      </div>
    </div>
  );
}

function FeatureTrans() {
  return (
    <div className="feature feature-trans">
      <span className="f-tag">
        <I.globe /> /번역
      </span>
      <h3 className="f-title">자동 번역, 108개 언어</h3>
      <p className="f-sub">
        채널을 통째로 자동 번역하거나, 메시지 하나만 누르면 즉시 옮겨드려요.
      </p>
      <div className="f-demo">
        <div className="trans-pair">
          <div className="row">
            <span className="lang">JP</span>
            <span className="body">こんばんは、サーバーへようこそ！</span>
          </div>
          <div className="row">
            <span className="lang">→ KO</span>
            <span className="arrow">↳</span>
            <span className="body">안녕하세요, 서버에 오신 걸 환영해요!</span>
          </div>
          <div className="row">
            <span className="lang">→ EN</span>
            <span className="arrow">↳</span>
            <span className="body">Good evening, welcome to the server!</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureEmoji() {
  return (
    <div className="feature feature-emoji">
      <span className="f-tag">
        <I.sparkle /> 이모지 업스케일링
      </span>
      <h3 className="f-title">작은 이모지가 다시 또렷하게</h3>
      <p className="f-sub">
        디스코드가 줄여놓은 커스텀 이모지를 원본 크기로 복원해서 보여줘요.
      </p>
      <div className="f-demo">
        <div className="emoji-grid">
          <div className="emoji-cell small">
            <div className="px" />
          </div>
          <span className="emoji-arrow">→</span>
          <div className="emoji-cell big">
            <div className="px" />
          </div>
          <span className="emoji-arrow" style={{ marginLeft: 16 }}>
            2x · 4x · 8x
          </span>
        </div>
      </div>
    </div>
  );
}

function FeatureWelcome() {
  return (
    <div className="feature feature-welcome">
      <span className="f-tag">
        <I.hand /> 환영 메시지
      </span>
      <h3 className="f-title">새로 온 친구에게, 따뜻한 첫 마디</h3>
      <p className="f-sub">
        멤버가 들어오면 환영 카드와 메시지를 자동으로 보내요. 카드 디자인과 멘트는 우리 서버 색에 맞게.
      </p>
      <div className="f-demo">
        <div className="welcome-card">
          <div className="av">M</div>
          <div>
            <div className="who">mochi_dev님이 합류했어요</div>
            <div className="when">just now · 14,288번째 멤버</div>
            <div className="num gowun">잘 부탁해요!</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureMusic() {
  return (
    <div className="feature feature-music">
      <span className="f-tag">
        <I.music /> 뮤직 플레이어 /p
      </span>
      <h3 className="f-title">음성채널 뮤직 플레이어</h3>
      <p className="f-sub">유튜브·사클·링크 그대로. 지금은 공사 중이에요.</p>
      <div className="f-demo">
        <div className="music-card">
          <div className="music-now">
            <div className="music-cover" />
            <div className="music-meta">
              <div className="music-title">夜に駆ける</div>
              <div className="music-artist">YOASOBI · 3:21</div>
            </div>
          </div>
          <div className="music-bar">
            <div className="fill" />
          </div>
          <div className="music-time">
            <span>1:14</span>
            <span>3:21</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Features() {
  return (
    <section id="features">
      <Container>
        <SectionHead
          eyebrow="FEATURES"
          title="한 봇으로, 우리 서버가 필요한 것 거의 다."
          sub="TTS, AI, 번역, 이모지, 환영, 음악 — 따로 봇을 6개 초대할 필요 없이 카미봇 하나로. 슬래시 한 번이면 어디서든 바로 쓸 수 있어요."
        />
        <div className="features">
          <FeatureAI />
          <FeatureTTS />
          <FeatureTrans />
          <FeatureEmoji />
          <FeatureWelcome />
          <FeatureMusic />
        </div>
      </Container>
    </section>
  );
}

export default Features;
